/**
 * ChartMind-Plus session helpers — patient completion markers + institutional turns.
 * Uses productId/source "chartmind-plus" so ChartMind originals stay isolated.
 */

import firebase from "firebase/compat/app";
import { db } from "services/firebase";
import {
  appendConversationTurn,
  createConversationSessionId,
} from "services/conversationSessionService";

export const PRODUCT_ID = "chartmind-plus";
export const DIFFERENTIAL_TCI_ID = "differential";

export { createConversationSessionId };

/**
 * Load completed ChartMind-Plus module sessions for the signed-in patient.
 * Stored under users/{uid}/chartMindPlusSessions with source: "chartmind-plus".
 */
export async function listChartMindPlusCompletions({ patientUid } = {}) {
  if (!patientUid) return [];
  try {
    const snap = await db
      .collection("users")
      .doc(patientUid)
      .collection("chartMindPlusSessions")
      .where("source", "==", PRODUCT_ID)
      .get();
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (err) {
    console.warn("[chartMindPlus] list completions failed", err?.message || err);
    return [];
  }
}

export function isModuleCompleted(completions, tciId) {
  const id = typeof tciId === "string" ? tciId.trim() : "";
  if (!id) return false;
  return completions.some(
    (row) => row?.tciId === id && row?.status === "completed",
  );
}

/**
 * Persist a ChartMind-Plus module completion (any tciId) for annotators + patient list.
 */
export async function persistChartMindPlusModuleCompletion({
  patientUid,
  sessionId,
  organizationId,
  institutionId,
  tciId,
  moduleLabel,
  cmrValues = {},
  confirmedValues = {},
  corrections = {},
} = {}) {
  if (!patientUid || !sessionId || !organizationId || !tciId) {
    throw new Error(
      "patientUid, sessionId, organizationId, and tciId are required.",
    );
  }

  const instId = institutionId || organizationId;
  const label = moduleLabel || tciId;
  const conversationTarget = `chartmind-plus-${tciId}`;
  const correctionKeys = Object.keys(corrections || {}).filter(
    (key) => corrections[key] != null && String(corrections[key]).trim() !== "",
  );

  const summaryLines = [
    `ChartMind-Plus ${label} module completed.`,
    `CMR values: ${JSON.stringify(cmrValues)}`,
    `Confirmed values: ${JSON.stringify(confirmedValues)}`,
    correctionKeys.length
      ? `Patient corrections (session only, CMR not updated): ${JSON.stringify(
          corrections,
        )}`
      : "Patient confirmed CMR values with no corrections.",
  ];

  await appendConversationTurn({
    sessionId,
    organizationId,
    institutionId: instId,
    productId: PRODUCT_ID,
    conversationTarget,
    tciId,
    turn: {
      turnIndex: 0,
      speaker: "system",
      text: summaryLines.join("\n"),
    },
  });

  await appendConversationTurn({
    sessionId,
    organizationId,
    institutionId: instId,
    productId: PRODUCT_ID,
    conversationTarget,
    tciId,
    turn: {
      turnIndex: 1,
      speaker: "user",
      text: JSON.stringify({
        confirmedValues,
        corrections,
        cmrValues,
      }),
    },
  });

  let completionId = null;
  try {
    const docRef = await db
      .collection("users")
      .doc(patientUid)
      .collection("chartMindPlusSessions")
      .add({
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        source: PRODUCT_ID,
        tciId,
        sessionId,
        status: "completed",
        institutionId: instId,
        organizationId,
        cmrValues: cmrValues || {},
        confirmedValues: confirmedValues || {},
        corrections: corrections || {},
      });
    completionId = docRef.id;
  } catch (err) {
    console.warn(
      "[chartMindPlus] completion marker write failed; conversation turns already saved",
      err?.message || err,
    );
  }

  return { completionId, sessionId };
}
